import React from 'react';
import '../styles/TrainingFilterCheck.css';

/**
 * "Does this filter select everything the panel reads from it?" — shown under each filter
 * slot on the Training Traceability config screen.
 *
 * Why it exists: a filter only returns the columns it SELECTS, and a column that isn't
 * selected comes back as an empty value rather than an error. So a filter missing one field
 * builds a clean, plausible, WRONG report — somebody reads as untrained on a document they
 * signed (Critical Note #24). The build already names such fields, but only after a job that
 * runs for minutes. This is the same answer in about a second, while the filter can still be
 * fixed.
 *
 * Three states, kept apart on purpose:
 *   checked + nothing missing  → the green line, an earned all-clear
 *   checked + missing          → the fields, by name, worst first, each with a way to fix it
 *   NOT checked                → why we can't tell. Never drawn as an all-clear.
 *
 * `Roles`, `Training List` and `Trainees` are per-tenant CUSTOM FIELD ids (`CS210` on the
 * reference account), so their physical name means nothing on another tenant and only the
 * label travels. When neither matches, we ask which column serves that purpose rather than
 * guessing — that answer is saved with the panel and the check then passes on the mapping.
 */

const SEVERITY_ORDER = ['required', 'recommended', 'optional'];

const SEVERITY_TEXT = {
  required: 'Required — the report will be wrong without it',
  recommended: 'Recommended — the panel builds, but something is lost',
  optional: 'Optional — a displayed value, never a verdict',
};

function MissingField({ field, columns, mappedTo, onMap }) {
  return (
    <li className={`tmfc-miss tmfc-${field.severity}`}>
      <div className="tmfc-miss-head">
        <span className="tmfc-miss-title">{field.title}</span>
        {field.name && <code className="tmfc-code">{field.name}</code>}
        {field.custom && (
          <span className="tmfc-tag" title="A custom field — its internal id differs per account, so only the column name travels.">
            custom field
          </span>
        )}
      </div>
      <p className="tmfc-why">{field.why}</p>
      {columns.length > 0 && (
        <label className="tmfc-map">
          <span>Already in this filter under another name?</span>
          <select
            value={mappedTo || ''}
            onChange={(e) => onMap(field.role, e.target.value)}
          >
            <option value="">— pick the column that holds it —</option>
            {columns.map((c) => (
              <option key={c.name} value={c.name}>
                {c.title}
                {c.title !== c.name ? ` (${c.name})` : ''}
              </option>
            ))}
          </select>
        </label>
      )}
    </li>
  );
}

function TrainingFilterCheck({ result, busy, hasFilter, mapping, onMap, onRecheck }) {
  if (!hasFilter) return null;

  if (busy) {
    return <div className="tmfc tmfc-busy">Checking this filter’s columns…</div>;
  }
  if (!result) return null;

  const recheck = (
    <button type="button" className="tmfc-recheck" onClick={onRecheck}>
      ↻ Re-check
    </button>
  );

  // Couldn't check. Say why — never draw this as an all-clear.
  if (!result.checked) {
    return (
      <div className="tmfc tmfc-unknown">
        <div className="tmfc-line">
          <span className="tmfc-icon" aria-hidden="true">?</span>
          <span>
            <strong>Columns not checked.</strong> {result.message}
          </span>
          {recheck}
        </div>
      </div>
    );
  }

  const missing = SEVERITY_ORDER.flatMap((s) => (result.missing?.[s] || []));
  const mappedCount = (result.fields || []).filter((f) => f.how === 'mapped').length;

  /* Smart refresh needs Created Date AND Updated Date on the filter, and their absence is
     NOT a correctness problem — the panel builds and scores exactly the same, it just has to
     re-read everything every time. So it is a separate, quieter line rather than another
     "missing column": worth saying while the filter is still being picked (a two-second fix
     in Orcanos), never worth blocking or alarming over. */
  const refreshNote = result.refresh && !result.refresh.ready ? (
    <div className="tmfc-refresh">
      <span className="tmfc-icon" aria-hidden="true">⤾</span>
      <span>
        No{' '}
        {(result.refresh.missing || []).map((m, i, all) => (
          <React.Fragment key={m}>
            {i > 0 && (i === all.length - 1 ? ' or ' : ', ')}
            <strong>{m}</strong>
          </React.Fragment>
        ))}{' '}
        column, so this filter can only ever be re-read in full — the panel will work, but{' '}
        <strong>Refresh</strong> will cost the same as <strong>Rebuild</strong>. Add{' '}
        {(result.refresh.missing || []).length > 1 ? 'them' : 'it'} in Orcanos to make
        refreshes read only what changed.
      </span>
    </div>
  ) : null;

  if (missing.length === 0) {
    return (
      <div className="tmfc tmfc-ok">
        <div className="tmfc-line">
          <span className="tmfc-icon" aria-hidden="true">✓</span>
          <span>
            All {(result.fields || []).length} columns this panel reads are in the filter
            {mappedCount > 0 && ` (${mappedCount} mapped by you)`}.
          </span>
          {recheck}
        </div>
        {refreshNote}
      </div>
    );
  }

  const nRequired = (result.missing?.required || []).length;

  return (
    <div className={`tmfc ${nRequired ? 'tmfc-bad' : 'tmfc-warn'}`}>
      <div className="tmfc-line">
        <span className="tmfc-icon" aria-hidden="true">{nRequired ? '!' : '⚠'}</span>
        <span>
          <strong>
            {missing.length} column{missing.length === 1 ? '' : 's'} missing
          </strong>{' '}
          from this filter
          {nRequired > 0 && (
            <>
              {' '}— <strong>{nRequired}</strong> of them required.
            </>
          )}
        </span>
        {recheck}
      </div>

      <p className="tmfc-fix">
        Open the filter in Orcanos, add the columns below to its layout, save it, then
        Re-check. If a column is already there under a different name, point at it here.
      </p>

      {SEVERITY_ORDER.map((sev) => {
        const group = result.missing?.[sev] || [];
        if (!group.length) return null;
        return (
          <div className="tmfc-group" key={sev}>
            <div className={`tmfc-group-head tmfc-${sev}-head`}>{SEVERITY_TEXT[sev]}</div>
            <ul className="tmfc-list">
              {group.map((f) => (
                <MissingField
                  key={f.role}
                  field={f}
                  columns={result.columns || []}
                  mappedTo={mapping?.[f.role]}
                  onMap={onMap}
                />
              ))}
            </ul>
          </div>
        );
      })}

      {mappedCount > 0 && (
        <p className="tmfc-mapped">
          {mappedCount} column{mappedCount === 1 ? '' : 's'} mapped by you:{' '}
          {(result.fields || [])
            .filter((f) => f.how === 'mapped')
            .map((f) => `${f.title} → ${f.matched_title || f.matched_name}`)
            .join(', ')}
        </p>
      )}
      {refreshNote}
    </div>
  );
}

export default TrainingFilterCheck;
